import * as React from 'preact/compat'
import Modal from '../Modal'
import { type GameInfo, type RelationType, type SeriesInfoInterface, types } from './types'
import SVG from '../SVG'
import Api from '../../api'
import { captureFetchException, loadStylesheet } from '../../common/helpers'
import commonCss from '../../../css/common.pcss.json'
import buttonsCss from '../../../css/buttons.pcss.json'
import formCss from '../../../css/form.pcss.json'
import styles from '../../../css/game-series.pcss.json'
import cn from 'classnames'
import { showToast } from '../toast'

interface PropTypes {
  onClose: () => void,
  game: GameInfo,
  series: SeriesInfoInterface,
  addGame: (game: GameInfo) => Promise<boolean>,
  showGameSearchModal: () => void,
  setGameAddCallback: (callback: null | ((game: GameInfo) => void)) => void,
  relatedGames?: Array<GameInfo & {
    relationType: number
  }>,
  relationTypes: Array<RelationType>,
  seriesGames: GameInfo[],
  reloadEditor: () => void
}

interface StateTypes {
  game: GameInfo,
  relatedGames: Array<GameInfo & {
    relationType: number
  }>
}

export default class GameRelationsModal extends React.Component<PropTypes, StateTypes> {
  state: StateTypes = {
    game: this.props.game,
    relatedGames: this.props.relatedGames || [],
  }

  modal = React.createRef<Modal>()

  componentWillMount() {
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('form')
  }

  componentDidMount() {
    this.props.setGameAddCallback(this.addRelatedGame)
  }

  componentWillUnmount() {
    this.props.setGameAddCallback(null)
  }

  addRelatedGame = (game: GameInfo) => {
    const relatedGames = [...this.state.relatedGames]
    if (game.id === this.props.game.id) {
      showToast({
        type: 'error',
        text: 'Нельзя связать игру с самой собой!'
      })
      return
    }
    if (relatedGames.find(game2 => game2.id === game.id)) {
      showToast({
        type: 'error',
        text: 'Связь с этой игрой уже существует'
      })
      return
    }
    const foundGame = this.props.seriesGames.find(game2 => game2.id === game.id);
    if (foundGame) {
      game = foundGame
    }
    relatedGames.push({
      ...game,
      relationType: null
    })
    this.setState({
      relatedGames
    })
  }

  generateOptions = (id: number) => {
    const options = [
      <option disabled selected={id === null}>Выбери категорию игры</option>
    ]
    for (const key in types) {
      options.push(<option value={key} selected={id === parseInt(key)}>{types[key]}</option>)
    }
    return options
  }

  generateRelationOptions = (id: number, gameType: number) => {
    const { relationTypes } = this.props
    const { game } = this.state
    const options = [
      <option disabled selected={id === null}>Выбери тип связи</option>
    ]
    for (const type of relationTypes) {
      const relationId = type.id
      if (type.forward_allowed_types.includes(game.type) && type.backward_allowed_types.includes(gameType)) {
        options.push(<option value={relationId} selected={id === relationId}>{type.forward_order_name}</option>)
      }
      if (type.forward_allowed_types.includes(gameType) && type.backward_allowed_types.includes(game.type)) {
        let name = type.backward_order_name
        if (name === 'Основная игра' || name === 'Оригинал') {
          name += ` (${type.forward_order_name})`
        }
        options.push(<option value={-relationId} selected={id === -relationId}>{name}</option>)
      }
    }
    return options
  }

  generateRelatedGamesList = () => {
    return this.state.relatedGames.map((game, index) => {
      const changeGameType = (e: React.TargetedEvent<HTMLSelectElement>) => {
        const type = e.currentTarget.value
        const relatedGames = [...this.state.relatedGames]
        relatedGames[index].type = parseInt(type) as keyof typeof types
        this.setState({
          relatedGames
        })
      }

      const changeGameRelationType = (e: React.TargetedEvent<HTMLSelectElement>) => {
        const type = e.currentTarget.value
        const relatedGames = [...this.state.relatedGames]
        relatedGames[index].relationType = parseInt(type)
        this.setState({
          relatedGames
        })
      }

      const removeRelatedGame = (e: React.TargetedEvent<HTMLButtonElement>) => {
        e.preventDefault()
        const relatedGames = [...this.state.relatedGames]
        relatedGames.splice(index, 1)
        this.setState({
          relatedGames
        })
      }

      return <div className={styles['relations-modal__related-card']}>
        <img className={styles['relations-modal__related-card__img']} alt='' src={game.poster}/>
        <p className={styles['relations-modal__related-card__title']}>{game.title}</p>
        <div className={styles['relations-modal__related-card__bottom-row']}>
          <select className={formCss['input']} onChange={changeGameType}>
            {this.generateOptions(game.type)}
          </select>
          <select className={formCss['input']} onChange={changeGameRelationType} disabled={game.type === null}>
            {this.generateRelationOptions(game.relationType, game.type)}
          </select>
        </div>
        <button
          type='button'
          className={cn(buttonsCss['icon'], buttonsCss['icon--low-contrast'], styles['relations-modal__related-card__remove'])}
          onClick={removeRelatedGame}
          style='--icon-size: 32px'
        >
          <SVG i='sg-new/cross' adaptive/>
        </button>
      </div>
    })
  }

  checkSaveDisabled = () => {
    const { game, relatedGames } = this.state
    let hasEmptyRelatedGame = false
    for (let relatedGame of relatedGames) {
      if (relatedGame.type === null || relatedGame.relationType === null) {
        hasEmptyRelatedGame = true
        break
      }
    }

    return game.type === null || hasEmptyRelatedGame
  }

  gameTypeChange = (e: React.TargetedEvent<HTMLSelectElement>) => {
    const type = e.currentTarget.value
    const oldType = this.state.game.type
    const newGame = { ...this.state.game }
    newGame.type = parseInt(type) as keyof typeof types
    this.setState({
      game: newGame
    }, () => {
      if (oldType !== null) {
        return
      }
      this.props.addGame(newGame)
        .then(() => {
          // do nothing
        }, () => {
          this.setState({
            game: {
              ...newGame,
              type: oldType
            }
          })
        })
    })
  }

  public saveForm = () => {
    const { series } = this.props
    const { game, relatedGames } = this.state
    Api.post({
      url: 'game-series/save-relations',
      body: {
        seriesId: series.id,
        gameId: game.id,
        type: game.type,
        relatedGames: relatedGames.map(relatedGame => ({
          id: relatedGame.id,
          type: relatedGame.type,
          relationType: relatedGame.relationType
        }))
      }
    })
      .then(() => {
        this.props.reloadEditor()
        // noinspection JSIgnoredPromiseFromCall
        this.modal.current.close()
      })
      .catch(error => {
        captureFetchException(error)
        showToast({
          type: 'error',
          text: 'При сохранении формы произошла ошибка'
        })
      })
  }

  render() {
    const { onClose, series, showGameSearchModal } = this.props
    const { game, relatedGames } = this.state

    return <Modal ref={this.modal} showClose onClose={onClose} mobileFullScreen={true}>
      <div className={styles['relations-modal']}>
        <h1 className={commonCss['h3']}>Редактирование связей</h1>
        <div className={styles['relations-modal__series-grid']}>
          <img className={styles['relations-modal__series-grid__img']} alt='' src={series.square_poster}/>
          <p className={styles['relations-modal__series-grid__text']}>Серия игр {series.name}</p>
        </div>
        <div className={styles['relations-modal__game-info']}>
          <img alt={game.title} src={game.poster} className={styles['relations-modal__game-info__img']}/>
          <div>
            <label className={formCss['label']}>
              Категория игры
            </label>
            <select className={formCss['input']} name='game_type' onChange={this.gameTypeChange}>
              {this.generateOptions(game.type)}
            </select>
          </div>
        </div>
        <button
          type='button'
          className={buttonsCss['low-contrast']}
          disabled={game.type === null}
          onClick={showGameSearchModal}
        >
          <SVG i='sg-new/plus' adaptive/> Привязать игру
        </button>
        <div className={styles['relations-modal__related-card-list']}>
          {this.generateRelatedGamesList()}
        </div>
        {relatedGames.length >= 3
          && <button
            type='button'
            className={buttonsCss['low-contrast']}
            disabled={game.type === null}
            onClick={showGameSearchModal}
          >
            <SVG i='sg-new/plus' adaptive/> Привязать игру
          </button>
        }
        <div className={styles['relations-modal__save-container']}>
          <button
            type='button'
            className={cn(buttonsCss['primary'], buttonsCss['big'])}
            disabled={this.checkSaveDisabled()}
            onClick={this.saveForm} style='height: 50px'
          >
            Сохранить
          </button>
        </div>
      </div>
    </Modal>
  }
}
